import React, { useState, useEffect, useId } from 'react';
import PropTypes from 'prop-types';

import { Input, InputContainer, Error, InputWrapper } from './style';
import { RegexpType, validator } from '../../../utils/validator';
import Icon from '../Icon';
import Text from '../Text';

const CustomInput = ({
  onBlur,
  onChange,
  onKeyPress,
  placeholder = '',
  value,
  name,
  type = 'text',
  valid = true,
  readonly = false,
  autoFocus = false,
  disabled,
  min,
  max,
  error = 'Il valore inserito non è corretto',
  label,
  icon,
  className,
  autocomplete = 'on',
  onClickIcon = () => {},
  size,
  isClearable = false,
  errorPosition = 'static',
  id,
  isSearch,
}) => {
  const [val, setVal] = useState(value);
  const [focus, setFocus] = useState(false);
  const [clickAfterCancel, setClickAfterCancel] = useState(false);

  const toggleFocus = () => setFocus((s) => !s);

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    if (clickAfterCancel) {
      onClickIcon();
      setClickAfterCancel(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val]);

  const updateValue = ({ currentTarget: { value: newValue } = {} }) => {
    if (newValue.length <= 0 || !validator(RegexpType.SPACEONLY, newValue)) {
      setVal(newValue);
      if (onChange) onChange(newValue.trim());
    }
  };

  const handleOnBlur = () => {
    toggleFocus();
    if (onBlur) onBlur(val);
  };

  const getDefaultError = () => 'Errore: il campo non è valido';

  const autoId = useId();
  const errorId = `${autoId}-errorId`;

  return (
    <InputWrapper size={size} role={isSearch && 'search'}>
      <InputContainer
        hasIcon={icon}
        valid={readonly || valid}
        value={val}
        readonly={readonly}
        disabled={disabled}
        error={error}
        focus={focus}
        className={`${className} ${isSearch ? 'search' : ''}`}
        size={size}
        as={isSearch && 'label'}
      >
        {icon && <Icon type={icon} onClick={onClickIcon} />}
        {isSearch && <span className="visually-hidden">Cerca</span>}
        <Input
          autoFocus={autoFocus}
          type={isSearch ? 'search' : type}
          onBlur={handleOnBlur}
          onFocus={toggleFocus}
          onChange={updateValue}
          onKeyPress={onKeyPress}
          placeholder={readonly ? '-' : placeholder}
          value={val}
          name={name}
          readonly={readonly}
          disabled={disabled}
          min={min}
          max={max}
          label={label}
          autoComplete={autocomplete}
          id={id}
          aria-errormessage={!valid && error ? errorId : undefined}
          aria-invalid={!valid && error ? 'true' : 'false'}
        />
        {isClearable && val?.length > 0 && (
          <Icon
            type="x"
            onClick={() => {
              setClickAfterCancel(true);
              updateValue({ currentTarget: { value: '' } });
            }}
          />
        )}
      </InputContainer>
      {!valid && error ? (
        <Error
          style={{ position: errorPosition }}
          id={errorId}
          aria-live="assertive"
          role="alert"
        >
          <Text type="b3">
            {typeof error === 'string' ? error : getDefaultError()}
          </Text>
        </Error>
      ) : null}
    </InputWrapper>
  );
};

CustomInput.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  valid: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  autoFocus: PropTypes.bool,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  icon: PropTypes.string,
  className: PropTypes.string,
  autocomplete: PropTypes.string,
  onClickIcon: PropTypes.func,
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['s', 'm', 'l', 'full']),
    PropTypes.number,
  ]),
  isClearable: PropTypes.bool,
  errorPosition: PropTypes.oneOf(['static', 'fixed', 'absolute', 'relative']),
  id: PropTypes.string,
  isSearch: PropTypes.bool,
};

export default CustomInput;
